import * as THREE from 'three';

/**
 * Brann's bet «El barril»: the beer the cask loses on the road. Wet amber puddles, each an irregular pool
 * with a glint, left where it dripped and slowly soaking into the ground. One instanced draw call, capped.
 */

const VERT = /* glsl */ `
attribute vec2 aFade; // alpha, seed
varying vec2 vUv;
varying vec2 vFade;
void main() {
  vUv = uv * 2.0 - 1.0;
  vFade = aFade;
  gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
}`;

const FRAG = /* glsl */ `
varying vec2 vUv;
varying vec2 vFade;
void main() {
  float a = atan(vUv.y, vUv.x);
  float s = vFade.y * 6.283;
  // A lumpy edge: a few lobes, different on each puddle.
  float edge = 0.78 + 0.12 * sin(a * 3.0 + s) + 0.07 * sin(a * 5.0 + s * 2.3);
  float r = length(vUv) / edge;
  if (r > 1.0) discard;
  vec3 deep = vec3(0.035, 0.013, 0.002); // linear: a dark stout brown on screen
  vec3 lip = vec3(0.28, 0.11, 0.015);
  vec3 c = mix(deep, lip, smoothstep(0.6, 0.95, r));
  // The glint of a wet surface.
  float g = 1.0 - smoothstep(0.0, 0.22, length(vUv - vec2(-0.25, 0.2)));
  c += vec3(0.9, 0.7, 0.45) * g * 0.35;
  // A foam lip round the edge.
  c = mix(c, vec3(0.55, 0.42, 0.25), smoothstep(0.9, 0.98, r) * 0.3);
  gl_FragColor = vec4(c, vFade.x * (0.92 - 0.25 * smoothstep(0.75, 1.0, r)));
  #include <colorspace_fragment>
}`;

interface Puddle {
  x: number;
  y: number;
  z: number;
  size: number;
  born: number;
  seed: number;
}

export class BeerLayer {
  readonly mesh: THREE.InstancedMesh;
  private list: Puddle[] = [];
  private fade: Float32Array;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private sc = new THREE.Vector3();
  private n = 0;

  constructor(private cap = 28, private lifeS = 16) {
    const geo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.fade = new Float32Array(cap * 2);
    geo.setAttribute('aFade', new THREE.InstancedBufferAttribute(this.fade, 2).setUsage(THREE.DynamicDrawUsage));
    const mat = new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
    this.mesh = new THREE.InstancedMesh(geo, mat, cap);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.renderOrder = 3;
  }

  /** A new puddle (`now` in seconds); the oldest goes when full. */
  add(x: number, y: number, z: number, size: number, now: number): void {
    if (this.list.length >= this.cap) this.list.shift();
    this.n++;
    this.list.push({ x, y, z, size, born: now, seed: (this.n * 0.618) % 1 });
  }

  /** Spreads fresh puddles out a little and soaks old ones away. */
  frame(now: number): void {
    this.list = this.list.length && now - (this.list[0] as Puddle).born > this.lifeS ? this.list.filter((p) => now - p.born <= this.lifeS) : this.list;
    const n = this.list.length;
    for (let i = 0; i < n; i++) {
      const p = this.list[i] as Puddle;
      const age = now - p.born;
      const grow = Math.min(1, 0.35 + age * 1.3);
      this.sc.set(p.size * grow, 1, p.size * grow);
      this.mesh.setMatrixAt(i, this.m.compose(this.v.set(p.x, p.y + 0.02, p.z), this.q, this.sc));
      this.fade[i * 2] = 1 - Math.max(0, (age - this.lifeS * 0.6) / (this.lifeS * 0.4));
      this.fade[i * 2 + 1] = p.seed;
    }
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
    (this.mesh.geometry.getAttribute('aFade') as THREE.InstancedBufferAttribute).needsUpdate = true;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
